import { createCatalog, setChannel, setChannelProducts } from 'actions/channel';
import { getProducts, setProducts } from 'actions/product';
import {
  setCountryShipping,
  setCurrencies,
  setLangDescription,
} from 'actions/translations';
import { updateCatalogProducts } from 'actions/user';
import message from 'components/utils/message';
import Pagination from 'components/utils/Pagination';
import Product from 'components/utils/Product';
import ProductMobile from 'components/utils/Product/ProductMobile';
import useResponsive from 'hooks/useResponsive';
import useTranslation from 'hooks/useTranslation';

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useDebounce } from '../../../hooks/useDebounce';
import ActionBar from './ActionBar';
import ActionBarMobile from './ActionBarMobile';
import style from './index.style';
import { useIsEmbedded } from '../../../hooks/useIsEmbedded';
import { getLocalStorage, setLocalStorage } from '../../../tools/localStorage';

import BannerCatalogo from './BannerCatalogo';
import useUserCashback from '../../../hooks/useUserCashback';
import CatalogTitle from './CatalogTitle';
import Breadcrumb from '../../generic/Breadcrumb';
import useFilters from '../../../hooks/useFilters';
import { useRouter } from 'next/router';
import DescriptionSeoCatalogo from './DescriptionSeoCatalogo';

export default function List({ publicList }) {
  const { TRANSLATIONS, locale } = useTranslation();
  const description = useSelector((state) => state?.description);
  const shippingCountry = useSelector((state) => state?.shippingCountry);
  const { isMobile, isTablet, isBigDesktop, isDesktop } = useResponsive();
  const isSmallDevice = isTablet || isMobile;
  const router = useRouter();
  const dispatch = useDispatch();
  const products = useSelector((state) => state?.products?.items);
  const [hasLoadedProducts, setHasLoadedProducts] = useState(false);
  const [renderProducts, setRenderProducts] = useState(
    Array(48).fill({ isLoadingProducts: true })
  );
  useEffect(() => {
    if (hasLoadedProducts) {
      setRenderProducts(products);
    }
  }, [hasLoadedProducts, products]);

  const totalPages = useSelector((state) => state?.products?.totalPages) || 1;
  const [selectedProducts, setSelectedProducts] = useState([]);

  const [selectAllAlert, setSelectAllAlert] = useState(false);
  const catalogs = useSelector((state) => state?.user?.userCatalogs) || [];
  const user = useSelector((state) => state?.user);
  const PERMISSIONS = useSelector((state) => state.permissions);
  const { userCashback } = useUserCashback();

  const totalGain = user?.withdrawableEcredit;
  const [catalogIndex, setCatalogIndex] = useState('-1');
  const handleOnChange = useCallback((value) => {
    setCatalogIndex(value);
  }, []);
  const newChannelToCreate = useSelector((state) => state?.channels);
  const [newChannel, setNewChannel] = useState({});
  const addresses = useSelector((state) => state.addresses);
  const shippingAddress =
    addresses?.length && addresses.find((address) => address.type === `1`);
  const { lang: urlLang } = router.query;

  const [lang, setLang] = useState(description);
  const [currency, setCurrency] = useState('EUR');
  useEffect(() => {
    const _currency = getLocalStorage('currency');
    if (_currency) {
      setCurrency(_currency);
    }
  }, []);

  const isEmbedded = useIsEmbedded();

  const handleChangeLang = useCallback(
    (lang) => {
      setLocalStorage(`lang_description`, lang);
      dispatch(setLangDescription(lang));
      setLang(lang);
    },
    [dispatch]
  );

  const handleCountryShip = useCallback(
    (country) => {
      setLocalStorage(`shipping_country`, country);
      dispatch(setCountryShipping(country));
    },
    [dispatch]
  );

  const handleCurrency = useCallback(
    (_currency) => {
      setLocalStorage(`currency`, _currency);
      dispatch(setCurrencies(_currency));
      setCurrency(_currency);
    },
    [dispatch]
  );

  useEffect(() => {
    if (newChannelToCreate?.name) {
      setNewChannel(newChannelToCreate);
      dispatch(setChannel({}));
      setCatalogIndex('-2');
    }
  }, [dispatch, newChannelToCreate]);
  const options = useMemo(() => {
    let options = [];
    if (catalogs?.length !== 0) {
      options = [
        ...catalogs?.map((catalog, index) => ({
          label: catalog?.name,
          value: index.toString(),
        })),
      ];
    }
    if (
      !!(PERMISSIONS.CAN_HAVE_3_LISTS && catalogs?.length < 3) ||
      !!(PERMISSIONS.CAN_HAVE_2_LIST && catalogs?.length < 2) ||
      !!(PERMISSIONS.CAN_HAVE_1_LIST && catalogs?.length < 1) ||
      PERMISSIONS.IS_ADMIN
    ) {
      options = [
        {
          label: catalogs?.length
            ? `${TRANSLATIONS.products.list.anotherList}`
            : `${TRANSLATIONS.products.list.firstList}`,
          value: '-1',
        },
        ...options,
      ];
    }
    if (newChannel?.name) {
      options.push({ label: newChannel?.name, value: '-2' });
    }
    return options;
  }, [
    PERMISSIONS.CAN_HAVE_1_LIST,
    PERMISSIONS.CAN_HAVE_2_LIST,
    PERMISSIONS.CAN_HAVE_3_LISTS,
    PERMISSIONS.IS_ADMIN,
    TRANSLATIONS.products.list.anotherList,
    TRANSLATIONS.products.list.firstList,
    catalogs,
    newChannel?.name,
  ]);

  const [cardList, setCardList] = useState('grid');
  const [searchText, setSearchText] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState('refId-desc');
  const debouncedSearchTextValue = useDebounce(searchText, 500);
  const [isLoadingProducts, setIsLoadingProducts] = useState(true);

  const requestProducts = useCallback(() => {
    if (!router.isReady) return;
    setIsLoadingProducts(true);
    setRenderProducts(Array(48).fill({ isLoadingProducts: true }));
    const queryParams = { ...router.query };
    let queryPage = queryParams.page;
    delete queryParams?.id;
    delete queryParams?.tab;
    delete queryParams?.page;
    delete queryParams?.lang;
    setFilters(queryParams);
    const acceptedlocales = [locale];
    if (locale !== 'en_US') {
      acceptedlocales.push('en_US');
    }
    let params = {
      page: queryPage || 1,
      pageSize: 48,
      q: debouncedSearchTextValue,
      sort,
      currency,
      acceptedlocales: acceptedlocales.join(','),
      priceRef: 'bestTaxable',
    };
    if (!!Object.keys(queryParams).length) {
      params = { ...params, ...queryParams };
    }
    dispatch(
      getProducts(
        params,
        (data) => {
          dispatch(setProducts(data));
          setTimeout(() => {
            setHasLoadedProducts(true);
            setIsLoadingProducts(false);
          }, 200);
        },
        false
      )
    );
  }, [
    currency,
    debouncedSearchTextValue,
    dispatch,
    locale,
    router.isReady,
    router.query,
    sort,
  ]);

  useEffect(() => {
    requestProducts();
  }, [requestProducts]);

  const handleList = useCallback(() => {
    setCardList('list');
    setLocalStorage(`card-view`, cardList);
  }, [cardList]);

  const handleGrid = useCallback(() => {
    setCardList('grid');
    setLocalStorage(`card-view`, cardList);
  }, [cardList]);

  const isSelectedProduct = useCallback(
    (_id) => selectedProducts.includes(_id),
    [selectedProducts]
  );

  const onClickRemoveButton = useCallback(
    (_id) => {
      const tmpSelectedProducts = [...selectedProducts];
      const index = tmpSelectedProducts.indexOf(_id);
      if (index >= 0) {
        tmpSelectedProducts.splice(index, 1);
        setSelectedProducts(tmpSelectedProducts);
      }
      setSelectAllAlert(false);
    },
    [selectedProducts]
  );

  const onChangePage = useCallback(
    (newPage) => {
      let url = router.asPath;
      const { page } = router.query;
      if (page) {
        url = url.replace(/page=\d+/i, `page=${newPage}`);
      } else {
        if (window.location.search.length) {
          url = url.concat(`&page=${newPage}`);
        } else {
          url = url.concat(`?page=${newPage}`);
        }
      }
      router.push(url);
      // setPage(newPage);
      // setSelectAllAlert(false);
    },
    [router]
  );

  const onClickSelectAll = useCallback(
    (selectAllInCatalog = false) => {
      if (selectAllInCatalog) {
        const queryParams = router.query;
        const acceptedlocales = [lang];
        if (lang !== 'en_US') {
          acceptedlocales.push('en_US');
        }
        let params = {
          q: searchText,
          acceptedlocales: acceptedlocales.join(','),
          sort,
        };
        if (!!Object.keys(queryParams).length) {
          params = { ...params, ...queryParams };
        }
        const limitProducts =
          parseInt(user?.roles?.[0]?.limit, 10) || undefined;
        delete params.page;
        dispatch(
          getProducts({ ...params, onlyid: true }, (data) => {
            const newProducts = data.items || [];
            setSelectedProducts((prev) =>
              [
                ...new Set([
                  ...prev,
                  ...newProducts.map((product) => product.refId),
                ]),
              ].slice(0, limitProducts)
            );
          })
        );
        setSelectAllAlert(false);
      } else {
        setSelectedProducts(products.map((product) => product.id));
        setSelectAllAlert(true);
      }
    },
    [dispatch, lang, products, router.query, searchText, sort, user]
  );

  const onClickDeselectAll = useCallback(() => {
    setSelectedProducts([]);
    setSelectAllAlert(false);
  }, []);

  const onClickAddToList = useCallback(
    (index) => {
      if (index >= 0) {
        let tmpCatalog = catalogs?.[index];
        let allProducts = tmpCatalog?.allProducts;
        let queryRules = tmpCatalog?.queryRule;
        if (tmpCatalog?._id && selectedProducts?.length) {
          let catalogProductsCount = 0;
          dispatch(
            getProducts(
              { onlyid: true, user_catalog: tmpCatalog._id },
              (data) => {
                const catalogProducts =
                  data.items.flatMap((product) => product.refId) || [];
                const limitProducts =
                  parseInt(user?.roles?.[0]?.limit, 10) || 0;
                catalogProductsCount = catalogProducts.length;
                let tmpIds = [
                  ...new Set([...catalogProducts, ...selectedProducts]),
                ];
                const totalProducts =
                  selectedProducts.length === limitProducts
                    ? limitProducts
                    : catalogProductsCount + selectedProducts.length;
                if (selectedProducts.length === limitProducts) {
                  tmpIds = tmpIds.slice(0, limitProducts);
                }
                onClickDeselectAll();
                if (limitProducts && totalProducts > limitProducts) {
                  if (limitProducts === 100) {
                    message.error(
                      `${TRANSLATIONS.products.list.limit100products}`
                    );
                  } else if (limitProducts === 1000) {
                    message.error(
                      `${TRANSLATIONS.products.list.limit1000products}`
                    );
                  }
                } else if (allProducts || queryRules?.length > 0) {
                  if (allProducts) {
                    message.error(
                      `${TRANSLATIONS.products.list.automaticUpdate}`
                    );
                  } else {
                    message.warning(
                      `${TRANSLATIONS.products.list.queryRulesActive}`
                    );
                  }
                } else if (tmpIds?.length) {
                  dispatch(
                    updateCatalogProducts({
                      catalogId: tmpCatalog._id,
                      ids: tmpIds,
                      onComplete: () => {},
                    })
                  );
                  message.success(
                    `${selectedProducts?.length} ${TRANSLATIONS.products.list.product} ${TRANSLATIONS.products.list.catalog} ${tmpCatalog?.name}`
                  );
                }
              }
            )
          );
        }
      } else {
        if (index === '-1') {
          dispatch(setChannelProducts(selectedProducts));
          router.push(`/channel/new`);
        } else if (index === '-2') {
          const limitProducts = parseInt(user?.roles?.[0]?.limit, 10) || 0;
          if (limitProducts && selectedProducts?.length > limitProducts) {
            if (limitProducts === 1000) {
              message.error(`${TRANSLATIONS.products.list.limit100products}`);
            } else {
              message.error(`${TRANSLATIONS.products.list.limit1000products}`);
            }
            return false;
          } else {
            dispatch(
              createCatalog({
                ...newChannel,
                ids: selectedProducts,
                onSuccess: (data) => {
                  router.push(`/channel/${data?._id}/edit/products`);
                },
              })
            );
          }
        }
      }
    },
    [
      TRANSLATIONS.products.list.catalog,
      TRANSLATIONS.products.list.limit100products,
      TRANSLATIONS.products.list.limit1000products,
      TRANSLATIONS.products.list.product,
      TRANSLATIONS.products.list.automaticUpdate,
      TRANSLATIONS.products.list.queryRulesActive,
      catalogs,
      dispatch,
      newChannel,
      onClickDeselectAll,
      router,
      selectedProducts,
      user?.roles,
    ]
  );
  const onClickCheckbox = useCallback(
    (id) => {
      const tmpSelectedProducts = [...selectedProducts];
      const index = tmpSelectedProducts.indexOf(id);
      if (index >= 0) {
        tmpSelectedProducts.splice(index, 1);
        setSelectedProducts(tmpSelectedProducts);
      } else {
        tmpSelectedProducts.push(id);
        setSelectedProducts(tmpSelectedProducts);
      }
      setSelectAllAlert(false);
    },
    [selectedProducts]
  );

  const onSubmitFilters = useCallback(
    (values) => {
      const queryParams = Object.keys(values)
        .filter(
          (key) => values[key]?.length || typeof values[key] === 'boolean'
        )
        .map((key) => `${key}=${values[key]}`);
      queryParams.push('page=1');
      router.push(
        `product${
          queryParams.length ? '?' + queryParams.join('&') : ''
        }`.replace(/ /g, '+')
      );
      setFilters(values);
    },
    [router]
  );

  useEffect(() => {
    const filterTags = {};
    selectedTags.forEach((st) =>
      st.filter.forEach((filter) => {
        if (filterTags[filter.field]) {
          filterTags[filter.field] += ',' + filter.value;
        } else {
          filterTags[filter.field] = filter.value;
        }
      })
    );
  }, [selectedTags]);

  const refContainerProduct = useRef();

  const { optionsCategories, optionsSubCategories } = useFilters();
  const breadcrumbs = useMemo(() => {
    const breadcrumbs = [];
    if (!router.isReady) return breadcrumbs;

    const { category, subcategory, gender } = router.query;

    const categoryLabel = optionsCategories.find(
      (c) => c.value === category
    )?.label;
    const subcategoryLabel = optionsSubCategories.find(
      (c) => c.value === subcategory
    )?.label;
    const pathname = '/product';

    const query = {
      availability: 1,
    };
    if (gender) {
      query.gender = gender;
    }
    /* breadcrumbs.push({
      link: {
        pathname,
        query: {
          ...query,
        },
      },
      title: TRANSLATIONS.cart.catalog,
    });*/

    if (category && categoryLabel) {
      query.category = category;
      breadcrumbs.push({
        link: {
          pathname,
          query: {
            ...query,
          },
        },
        title: categoryLabel,
      });
    }
    if (subcategory && subcategoryLabel) {
      query.subcategory = subcategory;
      breadcrumbs.push({
        link: {
          pathname,
          query: {
            ...query,
            subcategory,
          },
        },
        title: subcategoryLabel,
      });
    }

    return breadcrumbs;
  }, [
    TRANSLATIONS.cart.catalog,
    optionsCategories,
    optionsSubCategories,
    router.isReady,
    router.query,
  ]);

  return (
    <div className="order-list-page">
      <Breadcrumb breadcrumbs={breadcrumbs} />
      <CatalogTitle filters={filters} />
      <BannerCatalogo refDiv={refContainerProduct} />
      {!isEmbedded && !publicList && (
        <>
          {!isSmallDevice && (
            <div className={'d-f jc-sb ai-c '}>
              {!!userCashback && (
                <div className="social-buttons d-f g-20">
                  <div className={'section-cashback d-f ai-c fd-c jc-c g-10'}>
                    <div className={'d-f ai-c jc-sb g-10'}>
                      <div className={'d-f ai-c g-5 cashback'}>
                        <img
                          src="/images/ecredit/Icon-ecredit.svg"
                          height={40}
                          alt={'Icon-ecredit'}
                        />
                        <p
                          style={{
                            color: 'var(--grey-dark)',
                          }}
                        >
                          {TRANSLATIONS.header.subheader.youEarn}{' '}
                          <span
                            style={{
                              fontSize: 15,
                              fontWeight: 800,
                            }}
                          >
                            {totalGain?.toFixed(2)} €
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
      {isDesktop || isBigDesktop ? (
        <ActionBar
          selectedProducts={selectedProducts}
          onClickSelectAll={onClickSelectAll}
          onClickDeselectAll={onClickDeselectAll}
          selectAllAlert={selectAllAlert}
          onClickAddToList={onClickAddToList}
          onClickIconList={handleList}
          onClickIconGrid={handleGrid}
          selectedTags={selectedTags}
          setSelectedTags={setSelectedTags}
          onSubmitFilters={onSubmitFilters}
          options={options}
          catalogIndex={catalogIndex}
          handleOnChange={handleOnChange}
          searchText={setSearchText}
          hasFiltered={Object.keys(filters).length}
          filters={filters}
          setSort={setSort}
          handleChangeLang={handleChangeLang}
          shippingAddress={shippingAddress}
          handleCountryShip={handleCountryShip}
          handleCurrency={handleCurrency}
          currency={currency}
          publicList={publicList}
        />
      ) : (
        <ActionBarMobile
          selectedProducts={selectedProducts}
          onClickSelectAll={onClickSelectAll}
          onClickDeselectAll={onClickDeselectAll}
          selectAllAlert={selectAllAlert}
          onClickAddToList={onClickAddToList}
          options={options}
          selectedTags={selectedTags}
          setSelectedTags={setSelectedTags}
          onSubmitFilters={onSubmitFilters}
          hasFiltered={Object.keys(filters)}
          catalogIndex={catalogIndex}
          handleOnChange={handleOnChange}
          onClickIconList={handleList}
          onClickIconGrid={handleGrid}
          searchText={setSearchText}
          filters={filters}
          setSort={setSort}
          handleChangeLang={handleChangeLang}
          shippingAddress={shippingAddress}
          handleCountryShip={handleCountryShip}
          handleCurrency={handleCurrency}
          currency={currency}
          publicList={publicList}
        />
      )}
      {!!renderProducts?.length || !hasLoadedProducts ? (
        <>
          <div className={'row'} id={'productId'} ref={refContainerProduct}>
            {!isSmallDevice ? (
              <>
                {renderProducts?.map((product) => (
                  <Product
                    key={product._id}
                    selectedProducts={selectedProducts}
                    product={product}
                    importList
                    isCatalog
                    onClickCheckbox={() => onClickCheckbox(product.id)}
                    viewList={cardList}
                    onClickRemove={
                      isSelectedProduct(product.id)
                        ? () => onClickRemoveButton(product.id)
                        : null
                    }
                    showDetail
                    removeButtonLabel={TRANSLATIONS.products.list.removeProduct}
                    activeButtonLabel={TRANSLATIONS.products.list.viewDetail}
                    selected={isSelectedProduct(product?.id)}
                    lang={lang}
                    handleChangeLang={handleChangeLang}
                    shippingCountry={shippingCountry}
                    currency={currency}
                    publicList={publicList}
                    isLoadingProducts={isLoadingProducts}
                  />
                ))}
              </>
            ) : (
              <>
                {renderProducts?.map((product) => (
                  <ProductMobile
                    key={product._id}
                    selectedProducts={selectedProducts}
                    product={product}
                    importList
                    onClickCheckbox={() => onClickCheckbox(product.id)}
                    viewList={cardList}
                    onClickRemove={
                      isSelectedProduct(product.id)
                        ? () => onClickRemoveButton(product.id)
                        : null
                    }
                    showDetail
                    removeButtonLabel={TRANSLATIONS.products.list.removeProduct}
                    activeButtonLabel={TRANSLATIONS.products.list.viewDetail}
                    selected={isSelectedProduct(product?.id)}
                    lang={lang}
                    handleChangeLang={handleChangeLang}
                    shippingCountry={shippingCountry}
                    currency={currency}
                    isCatalog
                    publicList={publicList}
                    isLoadingProducts={isLoadingProducts}
                  />
                ))}
              </>
            )}
          </div>
        </>
      ) : (
        <div className="d-f w-100 fw-w ai-c jc-sb">
          <div className="empty-card-wrapper d-f g-30 fw-w fd-c ai-c jc-c w-100">
            <img
              src="/images/product/icon-product-not-found.svg"
              alt="icon-product-not-found"
            />
            <h4>{TRANSLATIONS.channel.edit.noProductFound}</h4>
          </div>
        </div>
      )}
      <Pagination
        data={{
          currentPage: parseInt(router?.query?.page, 10) || 1,
          totalPages,
        }}
        onChange={onChangePage}
      />
      {locale === 'it_IT' ? <DescriptionSeoCatalogo /> : null}
      <style jsx>{style}</style>
    </div>
  );
}
